import React, { useContext } from 'react';
import {
    selectCapsuleSymbolBindingFromState,
    showNiceDate,
    WidgetPanelDetailProps,
} from 'widgets/supportVisualisation/Content/Widgets/WidgetPanel';
import JsonPanel from 'utils/components/JsonPanel/JsonPanel';
import {
    initialSymbolTileConfigState,
    SymbolTileConfigState,
} from 'widgets/symbolTile/store/instances/config/SymbolTileConfigState';
import { selectSymbolTileState } from 'widgets/symbolTile/store/data/selectors';
import {
    buildSymbolTileData,
    SymbolTileData,
    SymbolTileDataTimeSeriesItem,
} from 'widgets/symbolTile/components/symbolTileData';
import { WidgetPanelGrid } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelGrid';
import {
    SymbolTilesSymbolBarItem,
    SymbolTilesSymbolState,
} from 'widgets/symbolTile/store/data/SymbolTilesStreamState';

import AppContext from 'app/AppContext';
import { getExternalMarketBarData } from 'marketData/hooks';
import {
    selectIndigoMarketDataEnabled,
    selectLatestMarketTickData,
    selectMarketBarDataBars,
    selectRefData,
} from 'marketData/store/selectors';
import { SymbolTileMarketDataBarSize } from 'widgets/symbolTile/stream/SymbolTilesStreamManager';
import { useSymbolInBloombergSymbology } from 'symbols/symbolInfo/hooks';
import { SymbologyValues } from 'app/store/appSettings/AppSettingsState';

export interface WidgetPanelSymbolTileProps extends WidgetPanelDetailProps {
    mode: 'data' | 'bars' | 'timeSeries';
}

export default function WidgetPanelSymbolTile(
    props: WidgetPanelSymbolTileProps
) {
    const {
        widgetState,
        state,
        mode,
        widget: { id: widgetKey },
    } = props;

    const { numberFormat } = useContext(AppContext);

    const config =
        widgetState?.config ??
        (initialSymbolTileConfigState as SymbolTileConfigState);

    const symbolBinding = selectCapsuleSymbolBindingFromState(props);

    const bbSymbol = useSymbolInBloombergSymbology(
        symbolBinding?.symbol,
        symbolBinding?.symbology ?? SymbologyValues.Bloomberg
    );

    if (!symbolBinding) {
        return <></>;
    }

    const { source } = symbolBinding;

    const streamData: SymbolTilesSymbolState | undefined = bbSymbol
        ? selectSymbolTileState(state.state, bbSymbol)
        : undefined;

    const bars = bbSymbol
        ? selectMarketBarDataBars(
              state.state,
              bbSymbol,
              SymbolTileMarketDataBarSize
          )
        : undefined;
    const refData = selectRefData(state.state, bbSymbol);
    const indigoMarketDataEnabled = selectIndigoMarketDataEnabled(state.state);
    const dapiPriceData = getExternalMarketBarData(
        indigoMarketDataEnabled,
        bars,
        refData
    );
    const tickData = selectLatestMarketTickData(state.state, bbSymbol);

    const symbolTilesData: SymbolTileData | undefined = streamData
        ? buildSymbolTileData(
              numberFormat,
              streamData,
              dapiPriceData,
              tickData,
              config.showDataFor
          )
        : undefined;

    if (symbolTilesData) {
        showNiceDate(symbolTilesData, 'startTime');
        showNiceDate(symbolTilesData, 'endTime');

        for (const t of symbolTilesData.timeSeries) {
            showNiceDate(t, 'time');
        }
    }

    if (streamData) {
        for (const b of streamData.bars) {
            const withTime = b as unknown as { time: number };
            withTime.time = b._i * 60000;
            showNiceDate(withTime, 'time');
        }
    }

    if (mode === 'timeSeries') {
        return symbolTilesData ? (
            <WidgetPanelGrid<SymbolTileDataTimeSeriesItem>
                data={symbolTilesData.timeSeries}
                getRowDataId={(d) => d._i.toString()}
                csvFileName={`${widgetKey}-timeSeries`}
            />
        ) : (
            <></>
        );
    }

    if (mode === 'bars') {
        return symbolTilesData ? (
            <WidgetPanelGrid<SymbolTilesSymbolBarItem>
                data={streamData!.bars}
                getRowDataId={(d) => d._i.toString()}
                csvFileName={`${widgetKey}-bars`}
            />
        ) : (
            <></>
        );
    }

    return (
        <div className="widget-panel">
            <div className="title">Symbol Capsule Data</div>
            <div className="field">
                <span className={'label'}>Symbol:</span>
                {symbolBinding.symbol}
            </div>
            <div className="field">
                <span className={'label'}>Source</span>
                {source}
            </div>

            <JsonPanel data={symbolTilesData} />
        </div>
    );
}

export function addSymbolTilePanes(
    detailProps: WidgetPanelDetailProps,
    panes: { menuItem: string; render: () => JSX.Element }[]
) {
    const render = () => <WidgetPanelSymbolTile {...detailProps} mode="data" />;
    panes.push({
        menuItem: 'Data',
        render,
    });

    const renderTs = () => (
        <WidgetPanelSymbolTile {...detailProps} mode="timeSeries" />
    );
    panes.push({
        menuItem: 'Time Series',
        render: renderTs,
    });
    const renderB = () => (
        <WidgetPanelSymbolTile {...detailProps} mode="bars" />
    );
    panes.push({
        menuItem: 'Bars',
        render: renderB,
    });
}
